import React, { useState, Fragment, useEffect, useCallback, useRef, useImperativeHandle, useMemo, Suspense, lazy } from 'react';
import {
  Button as MButton,
  Typography,
  Select,
  MenuItem,
  Icon,
  TextField,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  FormGroup,
  InputAdornment,
  Tooltip,
  Toolbar,
  Box,
  Autocomplete as MuiAutocomplete,
  Menu,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { get, set, map, find, size, noop, flatten, isObject, last, keyBy, unset } from 'global/lib/lodash';
import clsx from 'clsx';
import { Access } from 'global/components/droprealms';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { DropzoneArea } from 'global/components/Dropzone';
import { isValidHex, _size, ensureArray, customSearch, realmsToString, pickFrom } from '../../utils';
import { ChromePicker } from 'react-color';
import { LoadingTools } from '../LoadingIcons';
import DropArea from 'global/components/drop-area';
import Image, { ImageList } from 'global/components/image';
import { isImgOrVid, splitMediaByType } from '../../utils/mediaUtils';
import { useStyles, Symbols } from './styles';
import { ColorSlider } from '../color-slider';
import { ImageSelectBtn } from 'feature/report/Assessment/utils';
import { Button, IconButton } from 'global/components/Button';
import DragSortableList from 'global/components/sortable-list';
import { debounce } from '../../lib/lodash';
import { TradeSelect } from '../trade-select';
import { AutoComplete } from '../Autocomplete';
import { DatePicker } from 'global/components/date-picker';
import { Dropitems, getRealmIcon } from '../droprealms';
import moment from 'moment';
import { DATE_RANGE_YEARS, getDateTimeFrameBounds, isDateWithinTimeFrame } from '../../utils/formatDate';
import { Media } from '../Media';
import { Loader, useScreen } from '../../styles';
import { uploadImageToPerform } from '../../../redux/actions/imageUploader';
import sendData from 'redux/actions/sendData';
import { FILEID } from '../drop-area';
import { awaitAll } from '../../utils/awaitAll';
import { Text } from '../text';
import SnackAlerts from 'global/components/snack-alerts';

const RichText = lazy(() => import(/* webpackChunkName: "rich-text" */ 'global/components/RichText'));

const DATE_RANGE_ERROR = `Date must be within ${DATE_RANGE_YEARS} years of today`;

const isEmptyFormValue = (inputVal) =>
  inputVal === undefined || inputVal === null || inputVal === '' || (isObject(inputVal) && !(inputVal instanceof Date) && !moment.isMoment(inputVal) && _size(inputVal) === 0);

const isEmptyRichTextValue = (inputVal) =>
  !String(inputVal || '')
    .replace(/<[^>]+>/g, '')
    .trim();

const isEmptyDisabledField = (input, value, inputProps) => {
  if (input.component && !input.type) return false;
  if (get(input, 'alwaysShow') || get(inputProps, 'alwaysShow') || get(inputProps, 'showAll')) return false;
  if (typeof inputProps.hideDisabledWhen === 'function' && inputProps.hideDisabledWhen(value)) return true;

  const isType = get(input, 'type');
  if (isType === 'RichText') return isEmptyRichTextValue(value);
  if (isType === 'DropZone') {
    const files = [...ensureArray(get(inputProps, 'initialFiles')), ...ensureArray(value)].filter(Boolean);
    return size(files) === 0;
  }
  if (isType === 'UserSelect') return size(ensureArray(value).filter(Boolean)) === 0;
  if (isType === 'Checkbox') return !(input.options || []).some((o) => get(value, o.value));
  return isEmptyFormValue(value);
};

const getDateFailReason = (inputVal) => {
  if (isEmptyFormValue(inputVal)) return null;
  const m = moment.isMoment(inputVal) ? inputVal : moment(inputVal);
  if (!m.isValid()) return 'invalidDate';
  if (!isDateWithinTimeFrame(m)) return 'dateRange';
  return null;
};

export const fixWeirdDateIssue = (d, addday = false) => {
  try {
    if (!d) {
      return d;
    }
    const dd = addday ? moment(d).add(1, 'day') : d;
    return moment(`${dd.toISOString().split('T')[0]}T00:00:00.000Z`);
  } catch (err) {
    console.warn(err);
    return d;
  }
};

export const convertJBuilderToObj = (d) => {
  const json = {};

  d.map((k) => {
    const { property, value, id } = k;
    set(json, [property], value);
    if (id) {
      set(json, 'id', id);
    }
  });

  return json;
};

export const convertObjToJbuilder = (obj) => {
  obj = obj || {};

  const values = [];

  Object.keys(obj).map((k) => {
    values.push({ property: k, value: obj[k] });
  });

  return values;
};

const symbols = [
  { name: 'cust-degree', content: '°', icon: <div style={Symbols.icon}>°</div>, title: 'Insert degree symbol' },
  { name: 'cust-tm', content: '™', icon: <div style={Symbols.icon}>™</div>, title: 'Insert trademark symbol' },
  { name: 'cust-copy', content: '©', icon: <div style={Symbols.icon}>©</div>, title: 'Insert copyright symbol' },
  { name: 'cust-rtm', content: '®', icon: <div style={Symbols.icon}>®</div>, title: 'Insert registered trademark symbol' },
];

// MUI v9 removed the top-level `InputProps`, `inputProps`, `InputLabelProps`,
// `FormHelperTextProps`, and `SelectProps` props on `TextField`; they're now
// consolidated under `slotProps`. Any leftover v8-style props get caught in
// `...other` and forwarded to the DOM as lowercase attributes (e.g.
// `inputprops="[object Object]"`), which silently drops `startAdornment`/
// `endAdornment`. This helper translates a props bag into the v9 shape.
const V8_TEXTFIELD_PROP_TO_SLOT = {
  InputProps: 'input',
  inputProps: 'htmlInput',
  InputLabelProps: 'inputLabel',
  FormHelperTextProps: 'formHelperText',
  SelectProps: 'select',
};
const toTextFieldProps = (props = {}) => {
  if (!props || typeof props !== 'object') return props;
  let hasV8 = false;
  for (const k of Object.keys(V8_TEXTFIELD_PROP_TO_SLOT)) {
    if (k in props) {
      hasV8 = true;
      break;
    }
  }
  if (!hasV8) return props;
  const { InputProps, inputProps, InputLabelProps, FormHelperTextProps, SelectProps, slotProps: existingSlotProps, ...rest } = props;
  const slotProps = { ...(existingSlotProps || {}) };
  const merge = (slot, extra) => {
    if (!extra) return;
    slotProps[slot] = { ...(slotProps[slot] || {}), ...extra };
  };
  merge('input', InputProps);
  merge('htmlInput', inputProps);
  merge('inputLabel', InputLabelProps);
  merge('formHelperText', FormHelperTextProps);
  merge('select', SelectProps);
  return { ...rest, slotProps };
};

// --- Repeater item: renders a child Form for each repeater entry ---
const RepeaterItem = ({ FormComponent, item, itemIndex, nestedInputs, disabled, onItemChange, onRemove, itemName, formStyle }) => {
  const onItemChangeRef = useRef(onItemChange);
  onItemChangeRef.current = onItemChange;
  const idxRef = useRef(itemIndex);
  idxRef.current = itemIndex;

  const handleFormState = useCallback((vals) => {
    onItemChangeRef.current(idxRef.current, vals);
  }, []);

  const screens = useMemo(
    () => [
      {
        inputs: nestedInputs.map((ni) => ({
          ...ni,
          defaultValue: () => {
            const val = item[ni.id];
            if (val !== undefined) return ni.type === 'DropArea' && !Array.isArray(val) ? [].concat(val) : val;
            if (ni.type === 'KeyboardDatePicker') return null;
            if (ni.type === 'DropArea') return [];
            return '';
          },
          props: { ...ni.props, disabled },
        })),
      },
    ],
    [disabled]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ height: 'auto' }}>
      <FormComponent showAllScreens isNoMarginContainer useHeight={false} onFormState={handleFormState} screens={screens} formStyle={formStyle} />
    </Box>
  );
};

const Form = React.forwardRef((props, ref) => {
  const theme = useTheme();
  const { showPosition, onSubmit, onFormState, loading, useStaticScreens } = props;
  const [formState, setFormState] = useState({});
  const [subState, setSubState] = useState({});
  const [screenIndex, setScreenIndex] = useState(0);
  const [_screens, setScreens] = useState(props.screens);
  const [collapsed, setCollapsed] = useState({});
  const formref = useRef();
  const latestRef = useRef({ formState, screens: props.screens, screenIndex: 0 });
  const [state, setState] = useState({ mediaOpen: false });
  const [alerts, setAlerts] = useState();
  // Sub-score images auto-save with the form. Caption has to reach the media
  // record as it is typed; other drop zones keep the caption until submit.
  const mediaCaptionPatch = useRef(null);
  if (!mediaCaptionPatch.current) {
    mediaCaptionPatch.current = debounce((mediaId, customProperties) => {
      if (mediaId == null || `${mediaId}`.includes('_file_')) return;
      sendData({
        url: `/media/${mediaId}`,
        method: 'patch',
        data: {
          data: {
            type: 'media',
            id: `${mediaId}`,
            attributes: {
              custom_properties: customProperties,
            },
          },
        },
      }).catch((err) => console.error(err));
    }, 1000);
  }
  useEffect(() => () => mediaCaptionPatch.current && mediaCaptionPatch.current.cancel(), []);

  useEffect(() => {
    const collap = {};
    props.screens.forEach((s, i) => {
      if (Array.isArray(s)) {
        s.forEach((scr, _i) => {
          set(collap, `${i}.${_i}`, get(scr, 'collapsable.collapsed', false));
        });
      } else {
        set(collap, i, get(s, 'collapsable.collapsed', false));
      }
    });
    setCollapsed(collap);
  }, []);

  const useHeight = get(props, 'useHeight', true);
  const sc = useScreen();
  const classes = useStyles({ useHeight, showPosition, ...sc });

  const screens = useStaticScreens ? _screens : get(props, 'screens', []);
  latestRef.current.screens = screens;
  latestRef.current.screenIndex = screenIndex;

  useEffect(() => {
    if (!props.updateStaticScreens) {
      return;
    }

    const buildInput = (input, i) => {
      if (Array.isArray(input)) {
        return input.map((inp, _i) => buildInput(inp, `${i}.${_i}`));
      }
      const propScreenInputs = get(props, `screens.${i}.inputs`, []) || [];
      const fromProps = propScreenInputs.find((si) => get(si, 'id', '').includes(input.id)) || {};
      const newprops = get(fromProps, 'props', input.props);

      return {
        ...input,
        defaultValue: get(fromProps, 'defaultValue', input.defaultValue),
        required: get(fromProps, 'required', input.required),
        props: newprops || {},
      };
    };

    const newscreens = [...screens].map((screen, i) => {
      return {
        ...screen,
        inputs: (screen.inputs || []).map((s) => buildInput(s, i)),
      };
    });

    setScreens(newscreens);
  }, props.updateStaticScreens || []);

  const showAllScreens = get(props, 'showAllScreens', false);
  const showNavigation = showAllScreens ? false : get(props, 'showNavigation', true);
  const canSubmit = showAllScreens && get(props, 'canSubmit', false);
  const isNoMarginContainer = get(props, 'isNoMarginContainer', false);

  const updateFS = useCallback(
    debounce((fs) => {
      onFormState && onFormState(fs);
    }, 1),
    []
  );

  const updateState = (id, val, update = true, notify = true) => {
    set(formState, id, val);
    if (requiredErrors.length && requiredErrors.includes(id)) {
      const isEmpty = val === undefined || val === null || val === '' || (isObject(val) && _size(val) === 0);
      if (!isEmpty) {
        setRequiredErrors((prev) => prev.filter((errId) => errId !== id));
      }
    }
    if (update) {
      setFormState({ ...formState });
    }
    latestRef.current.formState = formState;
    set(ref, 'current.formState', formState);
    if (notify) {
      updateFS(formState);
    }
  };

  const clearState = () => {
    Object.keys(formState).forEach((k) => {
      set(formState, k, undefined);
    });
    setFormState({});
    onFormState && onFormState(formState);
  };

  const addAnotherInput = (input, index) => {
    if (input.onAdd) {
      return input.onAdd();
    }

    const _screens = [...screens];
    const currentInputs = get(_screens, [screenIndex, 'inputs'], []);
    const newIndex = index + 1;

    //insert new input
    currentInputs.splice(newIndex, 0, {
      ...input,
      id: `${input.id}|${newIndex}`,
    });

    //delete items from input that should only be on the last one, which is what we inserted
    set(currentInputs, [index, 'add'], undefined);
    set(_screens, [screenIndex, 'inputs'], currentInputs);
    setScreens(_screens);
  };

  const removeInput = (input, index) => {
    if (input.onRemove) {
      return input.onRemove();
    }

    const _screens = [...screens];
    const currentInputs = get(_screens, [screenIndex, 'inputs'], []);
    const itemToRemove = currentInputs[index];

    //insert new input
    currentInputs.splice(index, 1);
    delete formState[get(itemToRemove, 'id')];

    //delete items from input that should only be on the last one, which is what we inserted
    set(currentInputs, [index - 1, 'add'], get(itemToRemove, 'add'));
    set(_screens, [screenIndex, 'inputs'], currentInputs);
    setScreens(_screens);
  };

  const getInputType = (screen, input, index, sIndex) => {
    if (Array.isArray(input)) {
      return <div className={classes.row}>{input.map((inp, i) => getInputType(screen, inp, i, sIndex))}</div>;
    }

    // if (input.useSource || screen.useSource) {
    //   input = get(props, `screens.${sIndex}.inputs.${index}`, input);
    // }

    const isType = get(input, 'type');
    let value = get(formState, input.id);
    if (value === undefined) {
      if (input.defaultValue && typeof input.defaultValue === 'function') {
        const val = input.defaultValue(formState);
        //this should prevent this from hitting an endless loop if the defaultValue
        //function happens to return undefined
        value = val === undefined ? null : val;
        updateState(input.id, value);
      }
    }

    const { helperText: _helperText, error: _errorProp, ...inputProps } = get(input, 'props', {});
    const isRequired = input.required || inputProps.required;
    const hasRequiredError = requiredErrors.includes(input.id);
    if (isRequired) {
      if (inputProps.label && !inputProps.label.endsWith('*')) {
        inputProps.label = `${inputProps.label} *`;
      } else if (!inputProps.label && inputProps.placeholder && !inputProps.placeholder.endsWith('*')) {
        inputProps.placeholder = `${inputProps.placeholder} *`;
      }
      unset(inputProps, 'required');
    }
    const dateFailReason = isType === 'KeyboardDatePicker' ? getDateFailReason(value) : null;
    const errorFromProps = typeof _errorProp === 'function' ? !!_errorProp(formState) : !!_errorProp;
    if (hasRequiredError || errorFromProps || dateFailReason) {
      inputProps.error = true;
    } else {
      unset(inputProps, 'error');
    }
    const helperText = hasRequiredError || dateFailReason ? _helperText || (dateFailReason ? DATE_RANGE_ERROR : 'This field is required') : _helperText;
    const helperTextType = helperText ? (typeof helperText === 'function' ? helperText(formState) : helperText) : undefined;

    let comp = null;

    const isDisabled = get(inputProps, 'disabled', false);
    const variant = isDisabled ? 'standard' : 'outlined';
    const isLast = index === screen.inputs.length - 1;
    const center = get(input, 'center', false);

    if (get(props, 'hideEmptyDisabled') && isDisabled && isEmptyDisabledField(input, value, inputProps)) {
      return null;
    }

    if (isType) {
      if (isType === 'RealmDialog') {
        const continueOnSelect = get(inputProps, 'continueOnSelect', true);
        comp = (
          <Box sx={{ width: '100%', height: '100%' }}>
            <Access
              key={index}
              disableListScroll
              {...inputProps}
              selected={value}
              onSelectedChange={(sel) => {
                updateState(input.id, sel);
                if (inputProps.singleSelect && continueOnSelect) {
                  setScreenIndex(screenIndex + 1);
                }
              }}
            />
          </Box>
        );
      } else if (isType === 'DropRealms') {
        const isSingle = get(inputProps, 'singleSelect', false);
        const getValue = () => {
          if (value && value.name) {
            return value;
          } else if (value) {
            return Object.keys(value).map((k) => get(value, k, {}));
          }
        };
        const getDisplayValue = () => {
          const v = getValue();
          if (v && v.name) {
            return v.name;
          } else if (v && Array.isArray(v)) {
            return realmsToString(v);
          }
        };
        const v = !!value ? getValue() : null;
        comp = (
          <Dropitems
            {...inputProps}
            selected={Array.isArray(value) ? keyBy(value, 'id') : value}
            render={(p) => {
              return (
                <FormControl fullWidth>
                  <InputLabel>{get(inputProps, 'label')}</InputLabel>
                  <Select
                    disabled={isDisabled}
                    label={get(inputProps, 'label')}
                    value={value}
                    renderValue={() => (
                      <div>
                        {!!value && isSingle && <Box sx={{ marginRight: '5px' }}>{getRealmIcon(v)}</Box>}
                        {!!value ? getDisplayValue() || get(inputProps, 'placeholder', 'Builder') : get(inputProps, 'placeholder', 'Builder')}
                      </div>
                    )}
                    open={false}
                    onOpen={(e) => {
                      if (!isDisabled) {
                        p.openModal(true);
                      }
                    }}
                    onChange={() => {}}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                  </Select>
                  {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
                </FormControl>
              );
            }}
            onUpdate={(sel) => updateState(input.id, sel)}
          />
        );
      } else if (isType === 'Select') {
        const data = typeof input.data === 'function' ? input.data() : input.data;
        comp = (
          <FormControl variant={variant} className={classes.formControl}>
            {inputProps.label && <InputLabel>{inputProps.label}</InputLabel>}
            <Select
              classes={{
                root: classes.width100,
              }}
              disableUnderline
              value={value || ''}
              onChange={(e) => updateState(input.id, e.target.value)}
              {...inputProps}
            >
              {map(data, (item, i) => {
                const d = input.item ? input.item(item) : item;
                return (
                  <MenuItem key={i} value={get(d, 'value', i)} disabled={get(d, 'disabled', false)}>
                    {get(d, 'title', '')}
                  </MenuItem>
                );
              })}
              {size(data) === 0 && <MenuItem>No Options</MenuItem>}
            </Select>
            {helperTextType && <FormHelperText error={hasRequiredError}>{helperTextType}</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'MultiSelect') {
        const data = typeof input.data === 'function' ? input.data() : input.data;

        const isDisabledData = isDisabled ? [''] : [];

        comp = (
          <FormControl variant="filled" className={classes.formControl}>
            {inputProps.label && <InputLabel>{inputProps.label}</InputLabel>}
            <div className={classes.formWrap}>
              <Select
                className={classes.width100}
                classes={{
                  root: classes.width100,
                }}
                multiple
                disableUnderline
                value={size(value) ? value : isDisabledData}
                onChange={(e) => {
                  const val = e.target.value.filter((f) => f);
                  if (size(val)) {
                    updateState(input.id, val);
                  }
                }}
                {...inputProps}
              >
                {map(size(data) ? data : isDisabledData, (item, i) => {
                  const d = input.item ? input.item(item) : item;
                  return (
                    <MenuItem key={i} value={get(d, 'value', i)}>
                      {get(d, 'title', '')}
                    </MenuItem>
                  );
                })}
                {size(data) === 0 && <MenuItem>No Options</MenuItem>}
              </Select>
              {value && (
                <div className={classes.multiSelectChipContainer}>
                  {value.map((sel, i) => {
                    const selected = find(data, (item) => {
                      const dd = input.item ? input.item(item) : item;
                      return dd.value === sel ? dd : false;
                    });
                    if (!selected) {
                      //item doens't exist in array so update formstate
                      value.splice(i, 1);
                      updateState(input.id, value);
                    }
                    const item = selected ? (input.item ? input.item(selected) : selected) : null;
                    return (
                      <div key={i} className={classes.multiSelectChipWrap}>
                        <Chip
                          key={sel}
                          size={'small'}
                          label={get(item, 'title', '')}
                          className={classes.multiSelectChip}
                          onDelete={
                            isDisabled
                              ? undefined
                              : () => {
                                  value.splice(i, 1);
                                  updateState(input.id, value);
                                }
                          }
                          {...get(input, 'chipProps', {})}
                        />
                        {input.chipTextAdd && <Typography variant={'caption'}>{input.chipTextAdd}</Typography>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {helperTextType && <FormHelperText error={hasRequiredError}>{helperTextType}</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'UserSelect') {
        const liveData = get(get(props, 'screens', []), [sIndex, 'inputs', index, 'data']);
        const inpData = liveData || input.data;
        const data = typeof inpData === 'function' ? inpData() : inpData;

        const sectoredUsers = (data || [])
          .filter((d) => !!get(d, 'contentRealm.closestRealm'))
          .reduce((a, item) => set(a, get(item, 'contentRealm.distance'), [...get(a, get(item, 'contentRealm.distance', []), []), item]), {});

        const sortedData = Object.keys(sectoredUsers).map((k) =>
          sectoredUsers[k]
            .sort((a, b) => {
              return get(a, 'name', '').localeCompare(get(b, 'name', ''));
            })
            .map((opt) => {
              return {
                ...opt,
                groupBy: `${get(opt, 'contentRealm.closestRealm')}${get(opt, 'contentRealm.belongsToUserRealm') === 0 ? ' - Other Companies' : ''}`,
              };
            })
            .sort((a, b) => {
              return get(a, 'groupBy', '').localeCompare(get(b, 'groupBy', ''));
            })
        );

        comp = (
          <FormControl variant="standard" error={hasRequiredError} className={classes.formControl}>
            <div className={!inputProps.disabled && classes.formWrap}>
              <AutoComplete
                classes={
                  inputProps.disabled
                    ? {
                        groupLabel: classes.primaryColor,
                        tag: classes.userSelectChip,
                        endAdornment: classes.collapsedScreen,
                      }
                    : {
                        groupLabel: classes.primaryColor,
                      }
                }
                variant="standard"
                multiple={get(inputProps, 'multiple', true)}
                options={flatten(sortedData)}
                groupBy={(opt) => opt.groupBy}
                getOptionLabel={(d) => {
                  const name = get(d, 'name', '');
                  if (isDisabled) {
                    const optIndex = ensureArray(value || []).findIndex((f) => f.id === get(d, 'id'));
                    const isLast = optIndex === size(ensureArray(value || [])) - 1;
                    return isLast ? name : `${name},`;
                  }
                  return name;
                }}
                getOptionKey={(d) => d.id}
                disableCloseOnSelect={get(inputProps, 'multiple', true)}
                onChange={(e, vals) => {
                  updateState(input.id, vals);
                }}
                value={value || []}
                renderInput={(params) => <TextField variant="standard" {...toTextFieldProps(inputProps)} onChange={noop} helperText={helperTextType} {...params} />}
                {...inputProps}
              />
            </div>
          </FormControl>
        );
      } else if (isType === 'TradeSelect') {
        comp = (
          <>
            {inputProps.label && (
              <InputLabel variant={'standard'} shrink={true} sx={{ backgroundColor: 'transparent' }}>
                {inputProps.label}
              </InputLabel>
            )}
            <TradeSelect {...inputProps} value={value || []} onChange={(v) => updateState(input.id, v)} />
            {helperTextType && <FormHelperText error={inputProps.error}>{helperTextType}</FormHelperText>}
          </>
        );
      } else if (isType === 'Autocomplete') {
        const liveData = get(get(props, 'screens', []), [sIndex, 'inputs', index, 'data']);
        const inpData = liveData || input.data;
        const data = typeof inpData === 'function' ? inpData() : inpData;
        // If the default value is a string that matches one of the data options, we set it to that option
        //   This reduces the complexity of setting a default value by allowing it to be passed as a string
        //   but is particularly valuable when setting the default from a previously entered string value (on form refresh)
        if (typeof value === 'string' || value instanceof String) {
          flatten(data).every((d) => {
            if (get(d, 'name') === value) {
              value = d;
              return false;
            }
            return true;
          });
        }

        if (inputProps.filterType === 'customsearch' && !inputProps.filterOptions) {
          set(inputProps, 'filterOptions', (opts, s) => customSearch(opts, s, {}, true));
        }

        const onBlur = () => {
          let sel = value;

          let isPotentiallyNew = typeof sel === 'string' || sel instanceof String;
          if (isPotentiallyNew) {
            sel = sel.trim();

            const hasMatch = flatten(data).find((r) => {
              return r.name.toLowerCase() == sel.toLowerCase();
            });

            if (hasMatch) {
              sel = hasMatch;
            }
          }

          updateState(input.id, sel);
        };

        comp = (
          <FormControl variant="standard" className={classes.formControl}>
            <div className={!inputProps.disabled && classes.formWrap}>
              <AutoComplete
                classes={
                  inputProps.disabled
                    ? {
                        groupLabel: classes.primaryColor,
                        tag: classes.userSelectChip,
                        endAdornment: classes.collapsedScreen,
                      }
                    : {
                        groupLabel: classes.primaryColor,
                      }
                }
                variant="standard"
                multiple={get(inputProps, 'multiple', false)}
                options={flatten(data)}
                getOptionLabel={(d) => {
                  if (typeof d === 'string') return d;
                  const name = get(d, 'label', '') || get(d, 'name', '');
                  if (isDisabled) {
                    const vals = ensureArray(value || []);
                    const optIndex = vals.findIndex((f) => f.id === get(d, 'id'));
                    const isLast = size(vals) === 1 ? true : optIndex === size(value) - 1;
                    return isLast ? name : `${name},`;
                  }
                  return name;
                }}
                getOptionKey={(d) => d.id}
                onChange={(e, vals) => {
                  updateState(input.id, vals);
                  inputProps.onSelectOption && inputProps.onSelectOption(vals);
                }}
                onInputChange={(e, val) => {
                  const useInputChange = get(inputProps, 'useInputChange', true);
                  if (!useInputChange) {
                    return;
                  }
                  // if the newly typed string matches an option, set the option as the value
                  // This reduces unecessary new creation when a value is typed the same as one in the option list
                  if (typeof val === 'string' || val instanceof String) {
                    const valLower = val.toLowerCase();
                    if (
                      flatten(data).every((d) => {
                        if (get(d, 'name').toLowerCase() === valLower) {
                          updateState(input.id, d);
                          return false;
                        }
                        return true;
                      })
                    ) {
                      updateState(input.id, val);
                    }
                  } else {
                    updateState(input.id, val);
                  }
                }}
                freeSolo={true}
                includeInputInList={true}
                renderInput={(params) => <TextField variant="standard" {...toTextFieldProps(inputProps)} onBlur={noop} onChange={noop} helperText={helperTextType} {...params} />}
                defaultValue={value}
                onBlur={onBlur}
                {...inputProps}
              />
            </div>
          </FormControl>
        );
      } else if (isType === 'TextField') {
        const maxLength = get(inputProps, 'inputProps.maxLength') ?? get(inputProps, 'slotProps.htmlInput.maxLength') ?? get(inputProps, 'htmlInput.maxLength');
        comp = (
          <TextField
            variant={isDisabled ? 'standard' : variant}
            onChange={(e) => {
              const raw = e.target.value;
              const next = maxLength != null ? `${raw}`.slice(0, maxLength) : raw;
              updateState(input.id, next);
            }}
            value={value}
            classes={{
              root: classes.itemRoot,
            }}
            {...toTextFieldProps({ InputLabelProps: { shrink: true }, ...inputProps })}
            helperText={helperTextType}
            minRows={!isDisabled && inputProps?.multiline ? inputProps?.minRows || 3 : undefined}
          />
        );
      } else if (isType === 'RichText') {
        if (isDisabled) {
          comp = (
            <FormControl disabled={true} className={clsx(classes.itemRoot, classes.block, 'MuiTextField-root')}>
              {!!get(inputProps, 'label') && (
                <InputLabel variant={'standard'} shrink={true}>
                  {get(inputProps, 'label')}
                </InputLabel>
              )}
              <Box
                sx={{ width: '100%', display: 'block' }}
                className={clsx('MuiInputBase-root MuiInput-root MuiInput-underline Mui-disabled Mui-disabled MuiInputBase-formControl MuiInput-formControl', classes.richTextDisabled)}
                dangerouslySetInnerHTML={{ __html: value || '<p></p>' }}
              />
            </FormControl>
          );
        } else {
          // RichText does not trigger re-render (update=false) to avoid infinite
          // looping, but does notify parent (notify=true) so callbacks receive
          // the latest value.
          const upd = (html) => {
            // Tiptap emits empty paragraphs ("<p></p>") for empty editors; treat
            // those as empty so the field clears when its contents are deleted.
            const stripped = (html || '').replace(/<[^>]+>/g, '').trim();
            const isEmpty = !stripped;
            updateState(input.id, isEmpty ? '' : html, false, true);
          };
          const { label: richLabel, placeholder: richPlaceholder, error: _richError, ...richInputProps } = inputProps || {};
          comp = (
            <FormControl error={hasRequiredError} component="div">
              {!!richLabel && (
                <InputLabel variant={'standard'} shrink={true} sx={{ position: 'relative', transform: 'none', fontSize: 12, marginBottom: '4px' }}>
                  {richLabel}
                </InputLabel>
              )}
              <FormControl
                error={false}
                className={clsx(classes.formControl, classes.formWrapHover)}
                sx={hasRequiredError ? { border: `1px solid ${theme.palette.error.main}`, borderRadius: '4px' } : undefined}
              >
                <div className={clsx('MuiFormControl-root MuiTextField-root', classes.itemRoot)}>
                  <div className="RichText">
                    <Suspense fallback={<Loader size={24} />}>
                      <RichText
                        defaultValue={value || ''}
                        onChange={upd}
                        onBlur={() => updateFS(formState)}
                        controls={['title', 'bold', 'italic', 'underline', 'strikethrough', 'highlight', 'undo', 'redo', 'link', 'media', 'numberList', 'bulletList', 'quote', 'code', 'clear']}
                        placeholder={richPlaceholder || undefined}
                        customControls={symbols}
                        {...richInputProps}
                      />
                    </Suspense>
                  </div>
                </div>
              </FormControl>
              {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
            </FormControl>
          );
        }
      } else if (isType === 'KeyboardDatePicker') {
        const changeDate = (e) => {
          if (inputProps.convert) {
            e = inputProps.convert(e);
          }

          updateState(input.id, e);
        };
        const { minDate, maxDate } = getDateTimeFrameBounds();
        comp = !isDisabled ? (
          <DatePicker
            variant={variant}
            autoOk={true}
            onChange={changeDate}
            classes={{
              root: classes.itemRoot,
            }}
            value={value || null}
            minDate={minDate}
            maxDate={maxDate}
            {...inputProps}
            slotProps={{
              textField: {
                error: get(inputProps, 'error', false),
                helperText: helperTextType,
              },
            }}
          />
        ) : (
          <TextField variant={'standard'} value={value ? moment(value).format('MM/DD/YYYY') : ''} {...toTextFieldProps(inputProps)} />
        );
      } else if (isType === 'ToggleButton') {
        comp = (
          <FormControl error={hasRequiredError} className={clsx(classes.toggleFormControl, center && classes.formControlCenter)}>
            {inputProps.label && (
              <InputLabel variant={'standard'} shrink={true} className={center ? classes.labelCenter : undefined}>
                {inputProps.label}
              </InputLabel>
            )}
            <ToggleButtonGroup
              classes={{
                root: classes.toggleBtnRoot,
              }}
              size="small"
              value={value}
              onChange={isDisabled ? undefined : (e, val) => val && updateState(input.id, val)}
              {...inputProps}
            >
              {input.options.map((opt, i) =>
                isDisabled ? (
                  opt.value === value ? (
                    <ToggleButton key={i} {...opt}>
                      {opt.title}
                    </ToggleButton>
                  ) : null
                ) : (
                  <ToggleButton key={i} {...opt}>
                    {opt.title}
                  </ToggleButton>
                )
              )}
            </ToggleButtonGroup>
            {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'DropZone') {
        comp = (
          <FormControl error={hasRequiredError} className={clsx(classes.formControl, 'dropZone')}>
            {inputProps.label && (
              <InputLabel variant={'standard'} shrink={true} className={classes.formInputLabel}>
                {inputProps.label}
              </InputLabel>
            )}
            {!isDisabled && <DropzoneArea onChange={(files) => updateState(input.id, files)} {...inputProps} showAlerts={['info', 'error']} />}
            {!!get(inputProps, 'initialFiles') && size((get(inputProps, 'initialFiles', []) || []).filter((f) => !!f)) > 0 && (
              <div className={clsx(classes.initialFiles, 'initialFiles')}>
                {(get(inputProps, 'initialFiles', []) || [])
                  .filter((f) => !!f)
                  .map((file, i) => {
                    if (get(inputProps, 'useChipsForPreview')) {
                      const renderChip = get(inputProps, 'renderChip');
                      if (renderChip) {
                        return renderChip({ key: i, file, inputProps });
                      }
                      return (
                        <Chip
                          key={i}
                          label={get(file, 'name')}
                          size="small"
                          onClick={() => {
                            window.open(file.fullUrl, '_blank');
                          }}
                          onDelete={isDisabled ? undefined : () => get(inputProps, 'onDeleteFile', noop)(file)}
                        />
                      );
                    }
                    const onDeleteFile = get(inputProps, 'onDeleteFile');
                    return (
                      <Box key={i} sx={{ position: 'relative', display: 'inline-block', marginRight: '8px' }}>
                        <Image src={file} useNaturalDims={false} alt={`image_${i}`} />
                        {!isDisabled && !!onDeleteFile && (
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => onDeleteFile(file)}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              backgroundColor: 'rgba(255,255,255,0.9)',
                              boxShadow: '0 0 4px rgba(0,0,0,0.2)',
                            }}
                            aria-label="Remove file"
                          >
                            <Icon fontSize="small">close</Icon>
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
              </div>
            )}
            {helperTextType && <FormHelperText sx={{ marginTop: '15px' }}>{helperTextType}</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'DropArea') {
        const stopProp = (e) => {
          e.stopPropagation();
          // e.preventDefault();
        };

        const deleteImage = (file, index) => {
          if (inputProps.confirmDelete) {
            const confirmed = window.confirm('Are you sure you want to remove this image?');
            if (!confirmed) {
              return;
            }
          }

          const photos = get(formState, input.id, []);
          photos.splice(index, 1);
          set(
            formState,
            input.id,
            photos.map((p) => ({ ...p, hasChanged: true }))
          );
          setFormState({ ...formState });
          updateState(input.id, get(formState, input.id));
        };

        const onSort = (order) => {
          order = (order || []).map((o) => ({ ...o, hasChanged: true }));
          updateState(input.id, order);
        };

        const onFilesChange = async (e) => {
          if (inputProps.uploadAllNew) {
            updateState(
              input.id,
              e.map((ee) => ({ ...ee, loading: true }))
            );
            e = await awaitAll(
              e.map((img) => () => async () => {
                if (img.file) {
                  const col = inputProps.bothMode ? (isImgOrVid(img) ? 'task-images' : 'task-files') : inputProps.collection;
                  const res = await uploadImageToPerform({
                    ...img,
                    collection: col,
                    custom_properties: {
                      caption: img.caption || get(img, 'custom_properties.caption', ''),
                      generated_conversions: {
                        large: true,
                        thumb: true,
                      },
                    },
                  });
                  return { ...res, ...get(res, 'attributes', {}) };
                }
                return img;
              }),
              {
                queueSize: 10,
              }
            );
          }
          if (inputProps.multiple === false) {
            updateState(
              input.id,
              [last(e)].filter((f) => !!f)
            );
          } else {
            updateState(input.id, e);
          }
        };

        const imageSelectSubmit = (e) => {
          const all = [
            ...get(formState, input.id, []),
            ...e.images.map((img) => ({
              ...pickFrom(img, [
                'assessment',
                'mediaId',
                'assessment_id',
                'collectionName',
                'createdAt',
                'custom_properties',
                'fullUrl',
                'largeUrl',
                'large-url',
                'id',
                'name',
                'quality_type',
                'realm',
                'realm_id',
                'realm_name',
                'realm_parent',
                'realm_tier',
                'scorecard_id',
                'scorecard_score_id',
                'superset',
                'superset_id',
                'task',
                'thumb-url',
                'thumbUrl',
                'updatedAt',
                'url',
                'user',
              ]),
              hasChanged: true,
            })),
          ];
          onFilesChange(all);
        };

        const accept = get(inputProps, 'accept', ['image/*', 'video/*']); //set accept to null to use this as not an image
        const isDocMode = !!inputProps.docMode;
        const isImageAccept = !isDocMode && (!accept || (Array.isArray(accept) ? accept : [accept]).some((a) => a.includes('image') || a.includes('video')));
        const isBothMode = !!inputProps.bothMode;

        const queueMediaCaption = (file, caption) => {
          if (!inputProps.autoSave) return;
          const mediaId = get(file, 'mediaId', get(file, 'id'));
          mediaCaptionPatch.current(mediaId, {
            ...(get(file, 'custom_properties') || {}),
            caption: caption || '',
          });
        };

        const onValuesChange = (o, baseLoc, index) => {
          // The arrows move between photos, but this callback was closed over the
          // thumbnail that opened the viewer. Apply the edit to the photo on screen.
          const filesNow = get(formState, input.id, []) || [];
          const sameId = (a, b) => a != null && b != null && String(a) === String(b);
          const matchIndex = (Array.isArray(filesNow) ? filesNow : []).findIndex((f) => f && (sameId(f.id, o.id) || sameId(f.mediaId, o.id)));
          const loc = matchIndex >= 0 ? `${input.id}.${matchIndex}` : baseLoc;
          const idx = matchIndex >= 0 ? matchIndex : index;

          if (o.archived) {
            deleteImage(o, idx);
          } else if (o.newimg) {
            set(formState, loc, { ...o.newimg, ...get(o, 'newimg.attributes', {}) });
            setFormState({ ...formState });
            updateState(input.id, formState[input.id]);
            setState((s) => ({ ...s, mediaOpen: false }));
          } else {
            set(formState, `${loc}.hasChanged`, true);
            const cap = get(o, 'values.custom_properties.caption');
            const caption = cap == null ? '' : cap;
            set(formState, `${loc}.caption`, caption);
            set(formState, `${loc}.custom_properties.caption`, caption);
            setFormState({ ...formState });
            if (inputProps.shouldUpdateOnCaptionChange) {
              updateState(input.id, get(formState, input.id));
            }
          }
        };

        const imageSelect = get(inputProps, 'imageSelect');
        const showCaption = get(inputProps, 'showCaption', true);
        const _filesRaw = get(formState, input.id, []) || [];
        const files = Array.isArray(_filesRaw) ? [..._filesRaw] : [];

        if (isDisabled) {
          const RenderFile = get(inputProps, '_renderFile');
          comp = (
            <FormControl className={clsx(classes.formControl, 'dropArea')}>
              {inputProps.label && (
                <InputLabel variant={'standard'} shrink={true}>
                  {inputProps.label}
                </InputLabel>
              )}
              <Box
                className={inputProps.inputStyles || ''}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  ...(inputProps.label ? { marginTop: '20px' } : {}),
                  ...(size(files) === 0 ? { minHeight: 8 } : {}),
                }}
              >
                {RenderFile ? (
                  files.map((file, i) => <RenderFile key={i} file={file} deleteImage={() => {}} valueKey={`${input.id}.${i}`} formState={[formState, setFormState]} />)
                ) : isBothMode ? (
                  (() => {
                    const { images: imgFiles, documents: docFiles } = splitMediaByType(files);
                    return (
                      <Box sx={{ width: '100%' }}>
                        {size(imgFiles) > 0 && <ImageList images={imgFiles} spaceBetween={inputProps.spaceBetween || 4} />}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                          {docFiles.map((file, i) => {
                            const fileName = get(file, 'name', get(file, 'fileName', 'Document'));
                            const url = get(file, 'fullUrl', get(file, 'url'));
                            return (
                              <Box key={`doc-${i}`} sx={{ margin: '4px' }}>
                                <Chip icon={<Icon>description</Icon>} label={fileName} onClick={() => url && window.open(url, '_blank')} />
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })()
                ) : !isImageAccept ? (
                  files.map((file, i) => {
                    const fileName = get(file, 'name', get(file, 'fileName', 'Document'));
                    const url = get(file, 'fullUrl', get(file, 'url'));
                    return (
                      <Box key={i} sx={{ margin: '4px' }}>
                        <Chip icon={<Icon>description</Icon>} label={fileName} onClick={() => url && window.open(url, '_blank')} />
                      </Box>
                    );
                  })
                ) : (
                  <ImageList images={files} spaceBetween={inputProps.spaceBetween || 4} />
                )}
              </Box>
              {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
            </FormControl>
          );
        } else {
          comp = (
            <FormControl error={hasRequiredError} className={clsx(classes.formControl, 'dropArea')}>
              {inputProps.label && (
                <InputLabel variant={'standard'} shrink={true} className={classes.formInputLabel}>
                  {inputProps.label}
                </InputLabel>
              )}
              <DropArea
                style={inputProps.label ? { marginTop: 20 } : undefined}
                accept={accept}
                docMode={isDocMode}
                // PFM-2680: on the Form `DropArea` (Task interface photo
                // dropzone) let clicks on the expanded box's empty space bubble
                // to the dropzone root so the whole box is click-to-upload. The
                // assessment-scoring CheckpointForm renders its own <DropArea>
                // directly (not via Form), so it is unaffected.
                bubbleEmptyClicks
                onFilesChange={onFilesChange}
                files={files}
                onSort={onSort}
                className={clsx(classes.dropZone, hasRequiredError && classes.dropZoneError)}
                renderContent={() => (
                  <Box sx={{ padding: '8px' }}>{isBothMode ? 'Drop or click to upload' : isImageAccept ? 'Drop images or click to upload' : 'Drop documents or click to upload'}</Box>
                )}
                renderFile={(file, key) => {
                  const RenderFile = get(inputProps, '_renderFile');
                  let i = get(formState, `${input.id}`, []).findIndex((f) => f.id == file.id);
                  if (i < 0) {
                    i = key;
                  }
                  const valueKey = `${input.id}.${i}`;
                  if (RenderFile && i !== -1) {
                    return <RenderFile file={file} key={key} deleteImage={() => deleteImage(file, i)} valueKey={valueKey} formState={[formState, setFormState]} />;
                  }

                  // In bothMode, check each file individually
                  const fileIsImage = isBothMode ? isImgOrVid(file) : isImageAccept;

                  if (!fileIsImage) {
                    return (
                      <div key={`${input.id}.${i}`} onClick={stopProp}>
                        <Chip label={get(file, 'file.name', get(file, 'name', get(file, 'fileName', '')))} onDelete={() => deleteImage(file, i)} sx={{ margin: '2px' }} />
                      </div>
                    );
                  }

                  const baseLoc = `${input.id}.${i}`;
                  const loc = `${baseLoc}.caption`;
                  const canDelete = get(file, 'canDelete', true);
                  const isLoading = inputProps.uploadAllNew && !!files.find((f) => `${f.id}`.includes(FILEID));

                  return (
                    // PFM-2680: the wrapper itself no longer swallows clicks, so
                    // clicks on its empty/margin space bubble to the dropzone root
                    // and open the file picker (the expanded box is fully
                    // click-to-upload). Propagation is stopped only on the
                    // interactive children below — the thumbnail (opens full
                    // screen), the caption field, and the delete button (inside
                    // the caption field's adornment).
                    <div key={loc} className={classes.imageWrap}>
                      {((accept && accept.includes('image') > -1) || isBothMode) && (
                        // Stop propagation around the thumbnail regardless of
                        // whether Media opens full screen (e.g. non-editable
                        // reference media) so a thumbnail click never reaches the
                        // root and opens the picker.
                        <div onClick={stopProp}>
                          <Media
                            {...inputProps}
                            {...file}
                            loading={isLoading}
                            images={files.filter((f) => (isBothMode ? isImgOrVid(f) : true))}
                            index={isBothMode ? files.filter((f) => isImgOrVid(f)).findIndex((f) => f.id == file.id) : i}
                            onValuesChange={(o) => onValuesChange(o, baseLoc, i)}
                            childImagesIncludedInEdit
                            onOpen={() => setState((s) => ({ ...s, mediaOpen: true }))}
                            onClose={() => setState((s) => ({ ...s, mediaOpen: false }))}
                          />
                        </div>
                      )}
                      {showCaption && !isLoading && !state.mediaOpen && (
                        <TextField
                          multiline
                          minRows={2}
                          variant="standard"
                          placeholder="caption"
                          // PFM-2680: stop clicks on the caption (and the delete
                          // button in its endAdornment) from bubbling to the
                          // dropzone root and opening the file picker.
                          onClick={stopProp}
                          // value={inputProps.shouldUpdateOnCaptionChange ? get(formState, loc) : undefined} //we are removing this because of this issue https://ibacos.atlassian.net/browse/PFM-1995
                          defaultValue={get(formState, loc)}
                          onChange={(e) => {
                            set(formState, `${baseLoc}.hasChanged`, true);
                            set(formState, loc, e.target.value);
                            set(formState, `${baseLoc}.custom_properties.caption`, e.target.value);
                            queueMediaCaption(file, e.target.value);
                            setFormState({ ...formState });
                            if (inputProps.shouldUpdateOnCaptionChange) {
                              updateState(input.id, get(formState, input.id));
                            }
                          }}
                          className={classes.comment}
                          slotProps={{
                            input: {
                              className: 'no-drag',
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    variant="text"
                                    toolTip={!canDelete ? "This media is a reference and can't be edited." : undefined}
                                    disabled={!canDelete}
                                    className={classes.imageDelete}
                                    onClick={() => deleteImage(file, i)}
                                    size="large"
                                  >
                                    <Icon>close</Icon>
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      )}
                    </div>
                  );
                }}
                {...inputProps}
              />
              {!!imageSelect && (
                <div className={'dropArea ImageSelect'}>
                  {(Array.isArray(imageSelect) ? imageSelect : [imageSelect]).map((selectProps, selectIndex) => (
                    <ImageSelectBtn key={selectIndex} {...selectProps} onSubmit={imageSelectSubmit} />
                  ))}
                </div>
              )}
              {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
            </FormControl>
          );
        }
      } else if (isType === 'RadioButton') {
        if (isDisabled) {
          comp = (
            <>
              <TextField
                variant={variant}
                value={get(
                  input.options.find((opt) => get(opt, 'value') === value),
                  'label',
                  value
                )}
                classes={{
                  root: classes.itemRoot,
                }}
                {...toTextFieldProps(inputProps)}
              />
              {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
            </>
          );
        } else {
          comp = (
            <FormControl error={hasRequiredError} className={classes.toggleFormControl}>
              {inputProps.label && (
                <InputLabel shrink={true} variant="standard">
                  {inputProps.label}
                </InputLabel>
              )}
              <RadioGroup
                classes={{
                  root: classes.toggleListRoot,
                }}
                size="small"
                value={value}
                onChange={isDisabled ? undefined : (e, val) => val && updateState(input.id, val)}
                {...inputProps}
              >
                {input.options.map((opt, i) => (
                  <FormControlLabel key={i} {...opt} control={<Radio />} />
                ))}
              </RadioGroup>
              {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
            </FormControl>
          );
        }
      } else if (isType === 'Checkbox') {
        comp = (
          <FormControl error={hasRequiredError} className={classes.toggleFormControl}>
            {inputProps.label && (
              <InputLabel shrink={false} className={classes.ourLabel}>
                {inputProps.label}
                {!!inputProps.tooltip && (
                  <Tooltip title={inputProps.tooltip.title}>
                    <Icon className={classes.infoIcon} color="primary">
                      info_circle
                    </Icon>
                  </Tooltip>
                )}
              </InputLabel>
            )}
            <FormGroup
              classes={{
                root: classes.toggleListRoot,
              }}
              size="small"
              {...inputProps}
            >
              {(isDisabled && !inputProps.showAll ? input.options.filter((o) => get(value, o.value)) : input.options).map((opt, i) => (
                <FormControlLabel
                  key={i}
                  {...opt}
                  control={
                    <Checkbox
                      onChange={isDisabled ? undefined : (e) => updateState(input.id, { ...value, [opt.value]: e.target.checked })}
                      disabled={isDisabled}
                      checked={isDisabled && !inputProps.showAll ? true : !!get(value, opt.value)}
                    />
                  }
                />
              ))}
            </FormGroup>
            {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'ColorSquare') {
        const color = `#${value}`;
        const isValid = isValidHex(color);
        const showColorPicker = !!get(subState, input.id);
        const onColorChange = (c, e) => {
          e.stopPropagation();
          e.preventDefault();
          updateState(input.id, c.hex.replace('#', ''));
        };
        const toggleColorPicker = () => {
          if (isDisabled) {
            return;
          }
          setSubState({ ...subState, [input.id]: !showColorPicker });
        };
        comp = (
          <TextField
            onClick={!showColorPicker && toggleColorPicker}
            variant={variant}
            error={!isValid}
            helperText={!isValid && `Invalid hex color`}
            onChange={(e) => updateState(input.id, e.target.value)}
            value={value}
            classes={{
              root: classes.itemRoot,
            }}
            {...toTextFieldProps({
              ...inputProps,
              InputProps: {
                startAdornment: <InputAdornment position="start">#</InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <Box onClick={toggleColorPicker} className={classes.squareColor} sx={{ backgroundColor: `#${value}` }} />
                    {showColorPicker && <div className={classes.colorPickerOverlay} onClick={toggleColorPicker} />}
                    {showColorPicker && (
                      <div className={classes.colorPicker}>
                        <ChromePicker color={color} disableAlpha={true} onChangeComplete={onColorChange} />
                      </div>
                    )}
                  </InputAdornment>
                ),
                ...(inputProps?.InputProps || {}),
              },
            })}
          />
        );
      } else if (isType === 'ColorSlider') {
        const onColorChange = (e, v) => {
          if (isDisabled) {
            return;
          }
          e.stopPropagation();
          e.preventDefault();
          updateState(input.id, v);
        };
        comp = (
          <FormControl className={classes.toggleFormControl}>
            {inputProps.label && (
              <InputLabel shrink={true} variant="standard">
                {inputProps.label}
              </InputLabel>
            )}
            <div className={classes.colorSliderWrap}>
              <ColorSlider {...inputProps} disabled={false} value={value} onChange={onColorChange} />
            </div>
          </FormControl>
        );
      } else if (isType === 'ChipAdd') {
        const maxItems = get(inputProps, 'maxItems');
        const items = Array.isArray(value) ? value : [];
        const validateItem = get(input, 'validateItem') || get(inputProps, 'validateItem');
        const getItemError = (item) => {
          const trimmed = String(item || '').trim();
          if (!trimmed || typeof validateItem !== 'function') return null;
          const result = validateItem(trimmed);
          if (result === true) return null;
          return typeof result === 'string' ? result : 'Invalid value';
        };
        const hasInvalidItem = items.some((item) => !!getItemError(item));
        const lastHasText = items.length > 0 && !!String(items[items.length - 1] || '').trim();
        const belowMax = maxItems == null || items.length < maxItems;
        const showGhost = !isDisabled && lastHasText && belowMax;
        const ghostEnabled = showGhost && !hasInvalidItem;
        // Enabled "+" row is just a trailing empty field in the list (avoids focus hacks).
        // Disabled "+" row is a separate overlay field until current items are valid.
        const displayItems = items.length === 0 && !isDisabled ? [''] : ghostEnabled ? [...items, ''] : items;

        const updateItems = (next) => updateState(input.id, next);

        const onItemChange = (index, newVal) => {
          if (index >= items.length) {
            updateItems([...items, newVal]);
            return;
          }
          if (items.length === 0) {
            updateItems([newVal]);
            return;
          }
          const next = [...items];
          next[index] = newVal;
          updateItems(next);
        };

        const onItemDelete = (index) => {
          const next = [...items];
          next.splice(index, 1);
          updateItems(next);
        };

        const onItemBlur = (index) => {
          const current = displayItems[index];
          const trimmed = String(current || '').trim();
          if (!trimmed) return;
          const error = getItemError(trimmed);
          if (error) {
            setAlerts({ message: error, severity: 'error' });
          }
          if (index < items.length && items[index] !== trimmed) {
            const next = [...items];
            next[index] = trimmed;
            updateItems(next);
          }
        };

        const onGhostDisabledActivate = () => {
          const invalid = items.find((item) => getItemError(item));
          setAlerts({
            message: getItemError(invalid) || 'Enter a valid value before adding another item',
            severity: 'error',
          });
        };

        comp = (
          <FormControl className={clsx(classes.toggleFormControl, inputProps.className)}>
            {inputProps.label && (
              <InputLabel variant={'standard'} shrink={true}>
                {inputProps.label}
              </InputLabel>
            )}
            <div className={classes.chipAddChipContainer}>
              {size(items) === 0 && isDisabled && <Typography>None</Typography>}
              {isDisabled && items.map((v, i) => <Chip size="small" key={i} label={v} {...get(input, 'chipProps', {})} />)}
              {!isDisabled &&
                displayItems.map((v, i) => {
                  const hasText = !!String(v || '').trim();
                  const isAddRow = ghostEnabled && i === displayItems.length - 1 && !hasText;
                  const atMaxOnly = maxItems != null && items.length >= maxItems && !hasText && i === displayItems.length - 1;
                  return (
                    <TextField
                      key={i}
                      variant={variant}
                      size="small"
                      disabled={atMaxOnly}
                      error={!!getItemError(v)}
                      helperText={atMaxOnly ? `Maximum of ${maxItems} items allowed` : undefined}
                      value={v || ''}
                      onChange={(e) => onItemChange(i, e.target.value)}
                      onBlur={() => onItemBlur(i)}
                      slotProps={{
                        input: {
                          placeholder: isAddRow ? '+' : hasText ? undefined : get(inputProps, 'placeholder'),
                          endAdornment: hasText ? (
                            <InputAdornment position="end">
                              <IconButton variant="text" color="error" size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => onItemDelete(i)} aria-label="Remove item">
                                <Icon fontSize="small">delete</Icon>
                              </IconButton>
                            </InputAdornment>
                          ) : undefined,
                        },
                      }}
                      classes={{
                        root: clsx(classes.itemRoot, classes.chipAddInputRow),
                      }}
                    />
                  );
                })}
              {showGhost && !ghostEnabled && (
                <div className={classes.chipAddGhostWrap}>
                  <TextField
                    variant={variant}
                    size="small"
                    disabled
                    value=""
                    slotProps={{ input: { placeholder: '+' } }}
                    classes={{
                      root: clsx(classes.itemRoot, classes.chipAddInputRow, classes.chipAddBtnInactive),
                    }}
                  />
                  <div className={classes.chipAddGhostOverlay} onClick={onGhostDisabledActivate} />
                </div>
              )}
            </div>
          </FormControl>
        );
      } else if (isType === 'ChipSelect') {
        const chipData = input.data || [];
        const allowCustomInput = !!input.allowCustomInput;
        const allowMultiple = input.allowMultiple !== undefined ? input.allowMultiple : true;
        const valArr = Array.isArray(value) ? value : value ? [value] : [];

        // Build option objects from chipData for the Autocomplete
        const allOptions = chipData.map((opt) => ({
          label: opt.label || opt.value || opt,
          value: opt.value || opt.label || opt,
        }));

        // Filter out already-selected options from the dropdown
        // In single-select mode, show all options so the user can pick a replacement
        const availableOptions = allowMultiple ? allOptions.filter((o) => !valArr.includes(o.value)) : allOptions;

        const removeValue = (val) => {
          updateState(
            input.id,
            valArr.filter((v) => v !== val)
          );
        };

        comp = (
          <FormControl error={hasRequiredError} component="div" className={clsx(classes.toggleFormControl, inputProps.className)}>
            {inputProps.label && (
              <InputLabel variant="standard" shrink sx={{ fontSize: 12 }}>
                {inputProps.label}
              </InputLabel>
            )}
            {!isDisabled && (
              <MuiAutocomplete
                multiple={allowMultiple}
                freeSolo={allowCustomInput}
                blurOnSelect={!allowMultiple}
                options={availableOptions}
                value={allowMultiple ? [] : null}
                getOptionLabel={(opt) => {
                  if (typeof opt === 'string') return opt;
                  return opt.label || opt.value || '';
                }}
                isOptionEqualToValue={() => false}
                onChange={(e, newVal) => {
                  if (allowMultiple) {
                    // newVal contains only the newly picked item(s) since value is always []
                    const added = (Array.isArray(newVal) ? newVal : [newVal]).filter(Boolean);
                    const newVals = added.map((v) => (typeof v === 'string' ? v : v.value));
                    updateState(input.id, [...valArr, ...newVals]);
                  } else {
                    const picked = typeof newVal === 'string' ? newVal : get(newVal, 'value');
                    updateState(input.id, picked ? [picked] : []);
                  }
                }}
                renderTags={() => null}
                renderInput={(params) => <TextField {...params} variant={variant} size="small" placeholder={get(inputProps, 'placeholder', 'Select...')} error={get(inputProps, 'error', false)} />}
                classes={{ root: classes.itemRoot }}
              />
            )}
            {valArr.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {valArr.map((v) => {
                  const opt = allOptions.find((o) => o.value === v);
                  const label = opt ? opt.label : v;
                  return <Chip size="small" key={v} label={label} onDelete={isDisabled ? undefined : () => removeValue(v)} />;
                })}
              </Box>
            )}
            {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'Repeater') {
        const { nestedInputs } = input;
        const itemName = get(inputProps, 'itemName') || get(inputProps, 'label') || 'Item';

        const handleItemChange = (idx, vals) => {
          // Read latest items from formState (mutated in place) to avoid stale closures
          const currentItems = [...(get(formState, input.id) || [])];
          const existingKey = get(currentItems, [idx, '_key']);
          // Extract values using lodash get (follows dot-path) but assign with literal keys
          // to avoid nested objects when field labels contain periods
          const item = {};
          nestedInputs.forEach((ni) => {
            const v = get(vals, ni.id);
            if (v !== undefined) item[ni.id] = v;
          });
          if (existingKey) item._key = existingKey;
          currentItems[idx] = item;
          updateState(input.id, currentItems);
        };

        const addItem = () => {
          updateState(input.id, [...(value || []), { _key: Date.now() }]);
        };

        const removeItem = (idx) => {
          const items = [...(value || [])];
          items.splice(idx, 1);
          updateState(input.id, items);
        };

        comp = (
          <FormControl error={hasRequiredError} className={clsx(classes.toggleFormControl, inputProps.className)}>
            {inputProps.label && (
              <InputLabel shrink={false} className={classes.ourLabel}>
                {inputProps.label}
              </InputLabel>
            )}
            {_helperText && (
              <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '8px' }}>
                {typeof _helperText === 'function' ? _helperText(formState) : _helperText}
              </Typography>
            )}
            {(value || []).map((item, idx) => (
              <Box key={item._key || idx} sx={{ marginBottom: '16px', paddingLeft: '16px', borderLeft: `3px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {itemName} #{idx + 1}
                  </Typography>
                  {!isDisabled && (
                    <IconButton variant="text" size="small" onClick={() => removeItem(idx)}>
                      <Icon>close</Icon>
                    </IconButton>
                  )}
                </Box>
                <RepeaterItem
                  FormComponent={Form}
                  item={item}
                  itemIndex={idx}
                  nestedInputs={nestedInputs}
                  disabled={isDisabled}
                  onItemChange={handleItemChange}
                  itemName={itemName}
                  formStyle={classes.formContainerFullWidth}
                />
              </Box>
            ))}
            {!isDisabled && (
              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginTop: '4px', gap: '4px' }} onClick={addItem}>
                <Typography variant="body2">Add Another</Typography>
                <Icon fontSize="small">add_circle</Icon>
              </Box>
            )}
            {hasRequiredError && <FormHelperText>This field is required</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'JsonBuilder') {
        const updateFieldValue = (i, key, val) => {
          set(value, `${i}.${key}`, val);
          updateState(input.id, value);
        };

        const { options } = input;

        const color = 'secondary';

        const renderValueField = (v, i) => {
          const selproperty = get(v, 'property', '');
          const currentOpts = get(
            options.find((o) => o.id === selproperty),
            'props',
            get(input, 'defaultProps', {})
          );

          const val = get(value, [i, 'value']);

          const valueIsValid = (() => {
            let valid = val !== undefined && val !== '';
            const type = get(currentOpts, 'type', 'string');

            if (currentOpts.isValid) {
              valid = currentOpts.isValid(val);
            } else {
              if (type === 'duration') {
                const regex = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/gm;
                const matches = (val || '').match(regex);
                valid = !!size(matches);
              }

              if (type === 'number') {
                valid = !isNaN(parseInt(val));
              }

              if (type === 'string' && currentOpts.blankAllowed) {
                valid = true;
              }
            }

            return valid;
          })();

          const valObj = get(value, i);
          if (get(valObj, 'valid') !== valueIsValid) {
            updateFieldValue(i, 'valid', valueIsValid);
          }

          const onChange = (e) => {
            updateFieldValue(i, 'value', e.target.value);
            updateFieldValue(i, 'isDirty', true);
          };

          const helperText = !valueIsValid ? currentOpts.invalidText || 'Invalid format' : undefined;

          const standard = {
            ...currentOpts,
            variant: isDisabled ? 'standard' : 'outlined',
            size: 'small',
            disabled: isDisabled,
            error: !valueIsValid,
            helperText,
            onChange,
            value: val,
            sx: { flex: 1 },
          };

          if (!currentOpts.type || currentOpts.type === 'number') {
            return <TextField {...toTextFieldProps(standard)} />;
          } else if (currentOpts.type === 'Select') {
            return (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Select {...standard}>
                  {currentOpts.options.map((o) => (
                    <MenuItem value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
                {!!standard.error && <FormHelperText error>{standard.helperText}</FormHelperText>}
              </Box>
            );
          } else if (currentOpts.type === 'RichText') {
            const richUpd = (html) => {
              set(value, `${i}.value`, html);
              set(value, `${i}.isDirty`, true);
              updateState(input.id, value, false, false);
            };
            return (
              <Box className={clsx('RichText', classes.itemRoot)} sx={{ width: '100%', marginTop: '8px' }}>
                <Suspense fallback={<Loader size={24} />}>
                  <RichText
                    defaultValue={val || ''}
                    onChange={richUpd}
                    readOnly={isDisabled}
                    controls={isDisabled ? [] : ['bold', 'italic', 'underline', 'link', 'numberList', 'bulletList', 'clear']}
                  />
                </Suspense>
              </Box>
            );
          } else if (currentOpts.type === 'bool') {
            return (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Select {...standard}>
                  <MenuItem value={false}>False</MenuItem>
                  <MenuItem value={true}>True</MenuItem>
                </Select>
                {!!standard.error && <FormHelperText error>{standard.helperText}</FormHelperText>}
              </Box>
            );
          }

          return <TextField {...toTextFieldProps(standard)} />;
        };

        comp = (
          <FormControl className={clsx(classes.toggleFormControl, inputProps.className)}>
            {inputProps.label && (
              <InputLabel shrink={false} className={classes.ourLabel}>
                {inputProps.label}
                {!!inputProps.tooltip && (
                  <Tooltip title={inputProps.tooltip.title}>
                    <Icon className={classes.infoIcon} color="primary">
                      info_circle
                    </Icon>
                  </Tooltip>
                )}
              </InputLabel>
            )}
            {(value || []).map((val, i) => {
              const optProps = options.find((o) => o.id == val.property);
              const isRichText = get(optProps, 'props.type') === 'RichText';

              return !get(optProps, 'show', true) ? null : (
                <Box
                  key={val.property}
                  sx={{ border: isDisabled ? 'none' : `1px solid ${theme.palette.divider}`, borderRadius: '4px', padding: isDisabled ? '4px 0' : '12px', marginBottom: isDisabled ? '4px' : '12px' }}
                >
                  <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    {!!get(optProps, 'canDelete', true) && !isDisabled && (
                      <IconButton
                        size="small"
                        variant="text"
                        onClick={() => {
                          value.splice(i, 1);
                          updateState(input, value);
                        }}
                        sx={{ marginTop: '8px' }}
                      >
                        <Icon>close</Icon>
                      </IconButton>
                    )}
                    <FormControl variant={isDisabled ? 'standard' : 'outlined'} size="small" sx={{ minWidth: 160 }}>
                      {!isDisabled && <InputLabel>Select Option</InputLabel>}
                      <Select disabled={isDisabled} label={isDisabled ? undefined : 'Select Option'} value={val.property} onChange={(e) => updateFieldValue(i, 'property', e.target.value)}>
                        {options.map((opt, ii) => {
                          const hasKey = !!value.find((v) => v.property == opt.id);
                          return (
                            <MenuItem disabled={hasKey} key={opt.id} value={opt.id}>
                              {opt.label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    {!isRichText && renderValueField(val, i)}
                  </Box>
                  {isRichText && renderValueField(val, i)}
                </Box>
              );
            })}
            {!isDisabled && (
              <Button {...get(input, 'addBtn', {})} sx={{ marginTop: '4px' }} onClick={() => updateState(input.id, [...(value || []), {}])} startIcon={<Icon>edit</Icon>}>
                {get(input, 'addBtn.title', 'ADD MORE')}
              </Button>
            )}
          </FormControl>
        );
      } else {
        console.error('COMPONENT TYPE NOT SETUP!!');
      }
    } else if (input.component) {
      if (inputProps.label) {
        comp = (
          <FormControl className={classes.toggleFormControl}>
            <InputLabel disableAnimation={true} shrink={true} variant={'standard'}>
              {inputProps.label}
            </InputLabel>
            {typeof input.component == 'function' ? input.component() : input.component}
          </FormControl>
        );
      } else {
        comp = typeof input.component === 'function' ? input.component() : input.component;
      }
    }

    const style = get(input, 'style', {});

    //return render
    const canAdd = typeof input.add === 'function' ? input.add(formState, input) : input.add;
    const canRemove = typeof input.remove === 'function' ? input.remove(formState, input) : input.remove;
    let buttonRow = null;

    if (canRemove || canAdd) {
      buttonRow = (
        <Box className={classes.row} sx={{ justifyContent: 'flex-start' }}>
          {canRemove && (
            <Button className={classes.addBtn} variant="outlined" color="error" onClick={() => removeInput(input, index)} startIcon={<Icon>delete_forever</Icon>}>
              {get(input, 'deleteBtn.title')}
            </Button>
          )}

          {canAdd && (
            <Button className={classes.addBtn} variant={get(input, 'addBtn.variant', 'outlined')} onClick={() => addAnotherInput(input, index)} startIcon={<Icon>add</Icon>}>
              {get(input, 'addBtn.title', 'Add Another')}
            </Button>
          )}
        </Box>
      );
    }

    return (
      <div key={input.id} className={clsx(classes.inputEach, input.inputStyle, 'inputEach', isDisabled && classes.inputDisabled, isLast && isDisabled && classes.inputDisabledLast)} style={style}>
        {comp}
        {buttonRow}
      </div>
    );
  };

  const [requiredErrors, setRequiredErrors] = useState([]);

  const commitPendingChipAdds = () => {
    let firstError = null;
    const walk = (input) => {
      if (Array.isArray(input)) {
        input.forEach(walk);
        return;
      }
      if (get(input, 'type') !== 'ChipAdd') return;
      const current = get(formState, input.id) || [];
      if (!Array.isArray(current)) return;
      const validateItem = get(input, 'validateItem') || get(input, 'props.validateItem');
      const sanitized = current.map((v) => String(v || '').trim()).filter(Boolean);
      if (typeof validateItem === 'function') {
        for (const item of sanitized) {
          const result = validateItem(item);
          if (result !== true && !firstError) {
            firstError = typeof result === 'string' ? result : 'Invalid value';
          }
        }
      }
      if (sanitized.length !== current.length || sanitized.some((v, i) => v !== current[i])) {
        updateState(input.id, sanitized);
      }
    };

    (latestRef.current.screens || []).forEach((screen) => {
      (Array.isArray(screen) ? screen : [screen]).forEach((s) => {
        (get(s, 'inputs') || []).forEach(walk);
      });
    });

    if (firstError) {
      setAlerts({ message: firstError, severity: 'error' });
    }
    return !firstError;
  };

  const validateRequired = () => {
    const chipAddsValid = commitPendingChipAdds();
    const allowed = canContinue(true);
    setRequiredErrors(allowed.passes ? [] : allowed.failed.map((f) => f.id));
    return chipAddsValid ? allowed : { passes: false, failed: allowed.failed };
  };

  const submit = async () => {
    const allowed = validateRequired();
    if (allowed.passes) {
      onSubmit && (await onSubmit(formState));
    }
  };

  const canContinue = (isSubmit = false) => {
    const _screens = latestRef.current.screens;
    const _screenIndex = latestRef.current.screenIndex;
    const _formState = latestRef.current.formState;

    if (!isSubmit && _screenIndex === _screens.length - 1) {
      return { passes: false, failed: [] };
    }

    const screenInputs = get(_screens, [_screenIndex, 'inputs'], []);
    let failed = [];
    let passes = true;
    const checkInput = (input) => {
      if (Array.isArray(input)) {
        input.forEach(checkInput);
        return;
      }
      const inputVal = get(_formState, input.id);
      const isRequired = input.required || get(input, 'props.required');
      const isEmpty = isEmptyFormValue(inputVal);
      const dateFailReason = input.type === 'KeyboardDatePicker' ? getDateFailReason(inputVal) : null;
      if ((isRequired && isEmpty) || dateFailReason) {
        passes = false;
        failed.push({ ...input, failReason: dateFailReason || 'required' });
      }
    };
    screenInputs.forEach(checkInput);
    return { passes, failed };
  };

  const requiredTooltip = (allowed) => {
    if (allowed.passes || !allowed.failed.length) return undefined;
    const dateFails = allowed.failed.filter((f) => f.failReason === 'dateRange' || f.failReason === 'invalidDate');
    const requiredFails = allowed.failed.filter((f) => f.failReason === 'required' || !f.failReason);
    if (dateFails.length && !requiredFails.length) return DATE_RANGE_ERROR;
    const labels = requiredFails.map((f) => (get(f, 'props.label') || f.id || '').replace(/\s*\*$/, ''));
    if (labels.length === 1) return `${labels[0]} is required`;
    if (labels.length === 2) return `${labels[0]} and ${labels[1]} are required`;
    return 'Required fields are missing';
  };

  const renderScreen = (screen, sIndex = 0) => {
    const title = get(screen, 'title');
    const description = get(screen, 'description');

    const titleStyle = get(screen, 'titleStyle');

    const collapsable = get(screen, 'collapsable');
    const colp = get(collapsed, sIndex);

    const CONT = get(screen, 'screenContainer.component', 'div');
    const CONT_PROPS = get(screen, 'screenContainer.props', {});

    const screenContainerStyle = get(screen, 'screenContainerStyle');

    const isFullScreenForm = get(screen, 'fullScreenView', false);

    const onSort = (order, event) => {
      screen.onSort && screen.onSort(order, event);
    };

    return (
      <CONT key={sIndex} {...CONT_PROPS} className={clsx(isFullScreenForm ? classes.screenSubContainerDialog : classes.screenSubContainer, get(CONT_PROPS, 'className', 'screenSubContainer'))}>
        {title && (
          <div className={clsx('titleContainer', classes.titleContainer)}>
            <div className={classes.titleRow}>
              <Text type={'title_large'} className={titleStyle}>
                {title}
              </Text>

              {collapsable && (
                <Button
                  className={classes.collapseButton}
                  size="small"
                  variant="text"
                  color="secondary"
                  endIcon={<Icon>{colp ? 'arrow_drop_down' : 'arrow_drop_up'}</Icon>}
                  onClick={() =>
                    setCollapsed({
                      ...set(collapsed, sIndex, !colp),
                    })
                  }
                  title={colp ? 'Show' : 'hide'}
                />
              )}
            </div>
            {description && <Typography variant="inherit">{description}</Typography>}
          </div>
        )}

        {screen && (
          <div className={clsx(classes.screenContainer, screenContainerStyle, colp && classes.collapsedScreen)}>
            {screen.sortable ? (
              <DragSortableList
                items={screen.inputs.map((input, index) => ({ content: getInputType(screen, input, index, sIndex), key: input.id }))}
                dropBackTransitionDuration={0.3}
                onSort={onSort}
                placeholder={<div className={`placeholderContent`}>DROP HERE</div>}
                type={'vertical'}
              />
            ) : (
              screen.inputs.map((input, index) => {
                return getInputType(screen, input, index, sIndex);
              })
            )}
          </div>
        )}
      </CONT>
    );
  };

  useImperativeHandle(ref, () => {
    return {
      updateState,
      clearState,
      formState,
      validateRequired,
      canContinue,
    };
  }, []);

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        ...(loading ? { display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 } : {}),
      }}
      ref={formref}
    >
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
      {loading ? (
        <div className={classes.formLoadingContainer}>
          <Typography variant={'h4'}>{get(props, 'loadingMessage', 'Please Wait...')}</Typography>
          <LoadingTools fontSize="large" color="primary" sx={{ marginTop: '10px', fontSize: '6rem' }} />
        </div>
      ) : (
        <div className={clsx(classes.formScrollContainer, 'formScrollContainer')}>
          {props.topContent}
          <div className={isNoMarginContainer ? clsx(classes.formContainerNoMargin, get(props, 'formStyle')) : clsx(classes.formContainer, get(props, 'formStyle'))}>
            {showAllScreens
              ? screens.map((screen, i) => {
                  if (Array.isArray(screen)) {
                    return (
                      <div key={i} className={clsx(classes.screenWrapper, 'screenWrapper', `screenWrapper_${i}`)}>
                        {screen.map((scr, _i) => renderScreen(scr, `${i}.${_i}`))}
                      </div>
                    );
                  }
                  return <div className={clsx(classes.screenWrapper, 'screenWrapper')}>{renderScreen(screen, i)}</div>;
                })
              : renderScreen(screens[screenIndex], screenIndex)}
          </div>
        </div>
      )}

      {!loading && (showPosition || showNavigation || canSubmit) && (
        <Toolbar position="fixed" sx={{ flexDirection: 'column', justifyContent: 'space-around', alignItems: 'center' }}>
          <Box>
            {showPosition && (
              <div className={classes.positionContainer}>
                {screens.map((screen, i) => {
                  const num = i + 1;
                  const posTitle = get(screen, 'positionTitle', get(screen, 'title', ''));
                  const isActive = i <= screenIndex;
                  const showBar = screens.length > 1 && num < screens.length;
                  const barActive = i < screenIndex;
                  return (
                    <Fragment key={i}>
                      <div className={classes.positionWrap}>
                        <div className={clsx(classes.posNumber, isActive && classes.backgroundPrimary)}>{num}</div>
                        <div className={clsx(classes.posTitle, isActive && classes.colorPrimary)}>{posTitle}</div>
                      </div>
                      {showBar && <div className={clsx(classes.bar, barActive && classes.colorPrimary)} />}
                    </Fragment>
                  );
                })}
              </div>
            )}

            {showNavigation && (
              <div className={classes.buttonsContainer}>
                <Button color="primary" variant="outlined" disabled={screenIndex === 0} className={classes.button} onClick={() => setScreenIndex(screenIndex - 1)}>
                  BACK
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={screenIndex === screens.length - 1}
                  className={classes.button}
                  sx={screenIndex !== screens.length - 1 && !canContinue().passes ? { opacity: 0.5 } : undefined}
                  toolTip={requiredTooltip(canContinue())}
                  onClick={() => {
                    const allowed = validateRequired();
                    if (allowed.passes) setScreenIndex(screenIndex + 1);
                  }}
                >
                  NEXT
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={screenIndex !== screens.length - 1}
                  className={classes.buttonFinish}
                  sx={screenIndex === screens.length - 1 && !canContinue(true).passes ? { opacity: 0.5 } : undefined}
                  toolTip={screenIndex === screens.length - 1 ? requiredTooltip(canContinue(true)) : undefined}
                  onClick={submit}
                >
                  FINISH
                </Button>
              </div>
            )}
          </Box>
          <Box sx={{ position: 'fixed', bottom: '8px', zIndex: 100, ...(props.submitBtnBoxStyle || {}) }}>
            {canSubmit && (
              <div className={classes.buttonsContainer}>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={props.useCheckContinue ? !canContinue(true).passes : false}
                  className={classes.buttonFinish}
                  sx={!props.useCheckContinue && !canContinue(true).passes ? { opacity: 0.5 } : undefined}
                  startIcon={get(props, 'submitIcon')}
                  onClick={submit}
                >
                  {get(props, 'submitText', 'SUBMIT')}
                </Button>
              </div>
            )}
          </Box>
        </Toolbar>
      )}
    </Box>
  );
});

export default Form;
